<template>
    <v-alert
        v-if="showAlert"
        :type="alertType"
        class="mx-auto text-center"
        style="position: fixed; top: 20px; left: 0; right: 0; z-index: 9999; max-width: 600px;"
        elevation="2"
        prominent
    >
        {{ alertMessage }}
    </v-alert>
    <v-container fluid>
        <v-card class="pa-4" elevation="2">
            <v-row>
                <v-col class="headline mb-4">
                    <h2>Campañas</h2>
                </v-col>
                <v-col cols="12" sm="6" style="text-align: right">
                    <v-btn color="primary" :loading="isImporting" :disabled="isImporting" @click="importCampaigns">
                        <template #loader>
                            <v-progress-circular indeterminate size="20" color="white" />
                        </template>
                        Importar
                    </v-btn>
                    <v-btn style="margin-left: 10px"  @click="$router.back()">Volver</v-btn>
                </v-col>
            </v-row>

            <!-- FILTRES FORA DE LA TAULA -->
            <v-row class="mb-4" dense>
                <v-col cols="12" md="3">
                    <v-text-field v-model="filters.name" label="Filtrar nombre" dense clearable/>
                </v-col>
                <v-col cols="12" md="3">
                    <v-text-field v-model="filters.client" label="Filtrar cliente" dense clearable/>
                </v-col>

                <v-col cols="12" md="3">
                    <div class="d-flex" style="gap: 8px;">
                        <v-text-field v-model="filters.startFrom" type="date" label="Inicio desde" dense clearable/>
                        <v-text-field v-model="filters.startTo"  type="date" label="Inicio hasta" dense clearable/>
                    </div>
                </v-col>

                <v-col cols="12" md="3">
                    <div class="d-flex" style="gap: 8px;">
                        <v-text-field v-model="filters.endFrom" type="date" label="Fin desde" dense clearable/>
                        <v-text-field v-model="filters.endtTo"  type="date" label="Fin hasta" dense clearable/>
                    </div>
                </v-col>
            </v-row>

            <v-data-table
                :items="filteredCampaigns"
                :headers="headers"
                class="elevation-1"
                item-value="id"
                no-data-text="No hay campañas disponibles"
            >
                <template v-slot:item.acciones="{ item }">
                    <v-btn size="small" color="secondary" class="ml-2" @click="viewLines(item)">
                        Ver líneas
                    </v-btn>
                </template>
            </v-data-table>
        </v-card>
    </v-container>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import axios from 'axios'

import { useRouter } from 'vue-router'

const isImporting = ref(false)

const router = useRouter()

import { useAlert } from '@/composables/useAlert'

const {
    alertMessage,
    alertType,
    showAlert,
    showAlertMessage
} = useAlert()

const campaigns = ref([])
const snackbar = ref(false)

const headers = [
    { title: 'Nombre', value: 'name' , sortable: true},
    { title: 'Cliente', value: 'client.name' , sortable: true},
    { title: 'Fecha inicio', value: 'start_date' , sortable: true},
    { title: 'Fecha fin', value: 'end_date' , sortable: true},
    { title: 'Acciones', value: 'acciones', sortable: false },
]

// --- FILTRES (fora de la taula) ---
const filters = reactive({
    name: '',
    client: '',
    startFrom: null, // 'YYYY-MM-DD'
    startTo: null,
    endFrom: null,
    endTo: null,
})

const normalize = (s) =>
    (s ?? '')
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')

const strIncludes = (hay, needle) =>
    !needle || normalize(hay).includes(normalize(needle))

const parseDate = (s) => {
    if (!s) return null
    const val = /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00` : s
    const d = new Date(val)
    return isNaN(d) ? null : d
}

const inRange = (dateStr, fromStr, toStr) => {
    if (!fromStr && !toStr) return true
    const d = parseDate(dateStr)
    if (!d) return false
    const from = parseDate(fromStr)
    const to = parseDate(toStr)
    if (from && d < from) return false
    if (to) {
        const toEnd = new Date(to)
        toEnd.setHours(23, 59, 59, 999) // incloure tot el dia
        if (d > toEnd) return false
    }
    return true
}

const filteredCampaigns = computed(() => {
    return campaigns.value.filter((c) => {
        const clientName = c?.client?.name ?? ''
        const okName = strIncludes(c?.name ?? '', filters.name)
        const okClient = strIncludes(clientName, filters.client)
        const okStart = inRange(c?.start_date, filters.startFrom, filters.startTo)
        const okEnd = inRange(c?.end_date, filters.endFrom, filters.endTo)
        return okName && okClient && okStart && okEnd
    })
})

const clearFilters = () => {
    filters.name = ''
    filters.client = ''
    filters.startFrom = null
    filters.startTo = null
    filters.endFrom = null
    filters.endTo = null
}

const fetchCampaigns = async () => {
    try {
        const {data} = await axios.get('/api/admin/campaigns', {
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
            }
        })
        campaigns.value = data
    }
    catch (e) {
        if (e.response?.status === 401) {
            localStorage.removeItem('token')
            await router.push('/')
        }
        showAlertMessage('Error al cargar campañas.', 'error')
    }
}

const importCampaigns = async () => {
    isImporting.value = true
    showAlertMessage('Importando datos de Odoo...', 'success')
    try {
        await axios.post('/api/admin/campaigns/import', {}, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
            }
        })
        await fetchCampaigns()
        showAlertMessage('Importación completada correctamente.', 'success')
    } catch (e) {
        console.error('Error al importar campañas:', e)
        showAlertMessage('Error al importar campañas desde Odoo.', 'error')
    } finally {
        isImporting.value = false
    }
}

const viewLines = (campaign) => {
    router.push({ name: 'campaignlines.index', params: { id: campaign.odoo_id } })
}

onMounted(fetchCampaigns)
</script>
